/**
 * Creates the Session that ranks a Track's locked Item Pool (BREAK 1 —
 * "Lock pool ✕ Rank in session").
 *
 * The only creation surface for this: it goes through
 * `createSessionWithFeedback` (the one path every session-creation surface
 * uses, see `createSessionFlow.callSites.test.ts`), pre-filled with
 * `intent: 'rank_priorities'` and the track's id. No separate poolId is
 * passed — the store's create action already resolves a rank_priorities
 * session's items from `itemPoolsStore`'s track-scoped pool whenever
 * `trackId` is set, so naming the track is naming the pool.
 * `TrackDetail`'s Item Pool tab and `ArrangeItemPoolPanel` both call this
 * instead of forking their own `createSessionWithFeedback` call.
 */
import { createSessionWithFeedback, type CreateSessionFlowResult, type ShowToastFn } from './createSessionFlow';

export interface StartTrackRankingSessionParams {
  trackId: string;
  /** Track display name, used to seed the session name ("Rank <track name>"). */
  trackName: string;
  cycleId?: string | null;
  userId: string;
  /** Navigates to the new session's detail route on success. */
  navigate: (path: string) => void;
  toast?: ShowToastFn;
}

export async function startTrackRankingSession(
  params: StartTrackRankingSessionParams,
): Promise<CreateSessionFlowResult> {
  const { trackId, trackName, cycleId, userId, navigate, toast } = params;
  return createSessionWithFeedback(
    {
      name: `Rank ${trackName}`,
      facilitatorId: userId,
      createdBy: userId,
      intent: 'rank_priorities',
      sessionContextMode: cycleId ? 'cycle_linked' : 'isolated',
      trackId,
      ...(cycleId ? { cycleId } : {}),
    },
    { navigate, toast },
  );
}
